import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Logger } from '../logger.service';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to all requests.
 *
 * @param req HttpRequest instance
 * @param next Next handler in the Http pipeline
 * @returns An Observable of the Http event stream
 */
export const errorHandlerInterceptor = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const snackBar = inject(MatSnackBar); // Dynamically inject MatSnackBar

  return next(req).pipe(catchError((error: HttpErrorResponse) => handleError(error, snackBar)));
};

/**
 * Handle the HTTP error appropriately
 * @param error The HttpErrorResponse instance
 * @param snackBar MatSnackBar instance for showing UI alerts
 * @returns An Observable throwing an error
 */
function handleError(error: HttpErrorResponse, snackBar: MatSnackBar): Observable<never> {
  let userFriendlyMessage = 'Something bad happened; please try again later.'; // Default message

  // Only handle actual errors (status codes >= 400 or network errors)
  // Ignore successful responses (200-299) that might incorrectly come through
  if (error.status >= 200 && error.status < 300) {
    // This shouldn't happen, but if it does, just re-throw without logging
    return throwError(() => error);
  }

  // IMPORTANT: Don't handle 401 errors here - let JwtErrorInterceptor handle them
  // This allows the token refresh flow to work properly
  if (error.status === 401) {
    return throwError(() => error);
  }

  // Pass through 429 (rate limit) so individual components can handle it with full context
  if (error.status === 429) {
    return throwError(() => error);
  }

  if (error.error instanceof ErrorEvent) {
    // A client-side or network error occurred. Handle it accordingly.
    console.error('An error occurred:', error.error.message);
    userFriendlyMessage = 'An error occurred on your device or network.';
  } else {
    // The backend returned an unsuccessful response code.
    console.error(`Backend returned code ${error.status}, body was:`, error.error);
    if (error.status === 403) {
      // no permission
      userFriendlyMessage = 'Your permission is not sufficient to view the requested resource. Please contact the System Administrator.';
    } else if (error.status >= 400) {
      if (error.error && error.error.error && error.error.msg) {
        // Legacy format: { error: '...', msg: '...' }
        userFriendlyMessage = error.error.msg;
      } else if (error.error && error.error.error) {
        // Standard PHP API format: { status: 'ERROR', error: '...' }
        userFriendlyMessage = error.error.error;
      } else if (error.error && error.error.message) {
        userFriendlyMessage = error.error.message;
      } else {
        userFriendlyMessage = 'There is a problem with the internet connection. Please check again.';
      }
    }
  }

  // Display a snack bar with the error message (only for actual errors, not 401)
  if (error.status >= 400) {
    snackBar.open(userFriendlyMessage, 'Close', {
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'my-snack-bar',
    });
  }

  return throwError(() => new Error(userFriendlyMessage));
}
